import React from 'react';
import { Navigate, useLocation, useParams } from 'react-router';

import { useAuth } from '../auth/useAuth';
import { useResolveJoinDestination } from './useResolveJoinDestination';

/**
 * Spec 026/027 — for an authenticated user, the path that comes AFTER
 * profile-setup or login when a pre-check isn't possible (no run code,
 * pre-check still loading, etc). `/dashboard` for the no-code path,
 * `/join/:code/team` (which auto-redirects to /team/find) for the
 * with-code path. The legacy GuestJoin route was deleted in spec 027,
 * so the team flow is the only option.
 *
 * When a run code IS present, callers should prefer the async
 * `useResolveJoinDestination` hook to also catch the
 * `team-ready → /runs/:runId/participant` short-cut.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function getNextAfterAuth(code: string | undefined): string {
  if (!code) return '/dashboard';
  return `/join/${code}/team`;
}

interface ProfileSetupRedirectProps {
  children: React.ReactNode;
}

/**
 * Spec 026 T032 — guards `/register` and `/join/:code/profile`: logged-in
 * users skip profile-setup and go straight to the next step (FR-Auth-Skip).
 *
 * Later-team-joining: if the user is already a member of a fully-named team
 * in this run, jump them directly into the run shell instead of dropping them
 * on FindTeam. The pre-check runs only when a run code is present.
 */
const ProfileSetupRedirect: React.FC<ProfileSetupRedirectProps> = ({
  children,
}) => {
  const { isAuthenticated, isLoading } = useAuth();
  const { code } = useParams<{ code?: string }>();
  const location = useLocation();
  const destination = useResolveJoinDestination(
    isAuthenticated && code ? code : undefined,
  );

  if (isLoading) return <>{children}</>;
  if (!isAuthenticated) return <>{children}</>;
  if (!code) return <Navigate to="/dashboard" replace />;
  if (destination.kind === 'loading') return null;
  if (destination.kind === 'team-ready') {
    return <Navigate to={`/runs/${destination.runId}/participant/stations`} replace />;
  }
  // 'team-pending' and 'no-team' both continue to the normal team flow.
  // Jump straight to /team/find (skipping the /team → /team/find auto-redirect
  // in App.tsx, which would drop the query string) so deep-link params like
  // `?team=<code>` (used by the QR-share flow) reach FindTeamStep's
  // preset-code handler. FindTeamStep is safe for pending users too — they
  // land back in the waiting screens via WS.
  return <Navigate to={{ pathname: `/join/${code}/team/find`, search: location.search }} replace />;
};

export default ProfileSetupRedirect;
